import { useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { MarketConfig } from '@/types';

export function MarketAdminForm({
  eventSlug,
  market,
  error,
}: {
  eventSlug: string;
  market: MarketConfig | null;
  error?: string;
}) {
  const { data, setData, patch, processing, transform } = useForm({
    initialBananas: market?.initialBananas ?? 1000,
    liquidityB: market?.liquidityB ?? 400,
    feeBps: market?.feeBps ?? 100,
    minTradeBananas: market?.minTradeBananas ?? 10,
    prizeTopPercent: market?.prizeTopPercent ?? 0,
    opensAt: market?.opensAt ?? '',
    closesAt: market?.closesAt ?? '',
  });

  const locked = market !== null && market.status !== 'setup';

  // The two `datetime-local` inputs hold wall-clock time in the admin's own zone; the
  // column is a timestamptz, so they are converted here exactly as the server action did.
  const submit = () => {
    transform((form) => ({
      ...form,
      opensAt: form.opensAt ? new Date(form.opensAt).toISOString() : '',
      closesAt: form.closesAt ? new Date(form.closesAt).toISOString() : '',
    }));
    patch(`/admin/${eventSlug}/market`, { preserveScroll: true });
  };

  return (
    <div className="flex flex-col gap-4">
      {locked && (
        <p className="bg-yellow-500/10 p-2 text-sm text-yellow-700 dark:text-yellow-400">
          Market is {market?.status}. Configuration is locked.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Initial bananas"
          help="Starting balance per hacker"
          value={data.initialBananas}
          disabled={locked || processing}
          onChange={(v) => setData('initialBananas', v)}
        />
        <Field
          label="Liquidity (b)"
          help="LMSR liquidity parameter — higher = flatter prices"
          value={data.liquidityB}
          disabled={locked || processing}
          onChange={(v) => setData('liquidityB', v)}
        />
        <Field
          label="Fee (bps)"
          help="100 = 1%"
          value={data.feeBps}
          disabled={locked || processing}
          onChange={(v) => setData('feeBps', v)}
        />
        <Field
          label="Min trade (🍌)"
          help="Minimum bananas per trade"
          value={data.minTradeBananas}
          disabled={locked || processing}
          onChange={(v) => setData('minTradeBananas', v)}
        />
        <Field
          label="Prize top %"
          help="Top N percent wins a special prize (0 = disabled)"
          value={data.prizeTopPercent}
          disabled={processing}
          onChange={(v) =>
            setData('prizeTopPercent', Math.min(100, Math.max(0, v)))
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>Opens at</Label>
          <Input
            className="rounded-none"
            type="datetime-local"
            value={data.opensAt}
            disabled={processing}
            onChange={(e) => setData('opensAt', e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Closes at</Label>
          <Input
            className="rounded-none"
            type="datetime-local"
            value={data.closesAt}
            disabled={processing}
            onChange={(e) => setData('closesAt', e.target.value)}
          />
        </div>
      </div>
      {error && (
        <p className="bg-destructive/10 p-2 text-destructive text-sm">{error}</p>
      )}
      <div className="flex gap-2">
        <Button className="rounded-none" onClick={submit} disabled={processing}>
          {processing ? 'Saving…' : market ? 'Update market' : 'Create market'}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  help,
  value,
  disabled,
  onChange,
}: {
  label: string;
  help: string;
  value: number;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Input
        className="rounded-none"
        type="number"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Math.floor(Number(e.target.value) || 0))}
      />
      <p className="text-muted-foreground text-xs">{help}</p>
    </div>
  );
}
